import { FC, Fragment, useState } from 'react'
import { Button } from './Button'
import { useTranslation } from 'react-i18next'
import { Box, useTheme, Dialog, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Icon } from 'packages/eid-icons'
import { IconButton } from 'packages/eid-ui'
import DomPurify from 'dompurify'
import ReactHtmlParser from 'react-html-parser'

const useModalStyles = makeStyles({
    modal: {
        backgroundColor: '#f7f8fa',
        borderRadius: '8px',
        wordBreak: 'break-all',
        padding: 0,
        '&:focus': {
            outline: 'none',
        },
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: '20px',
        padding: '16px 8px',
    },
    closeIcon: {
        display: 'flex',
        marginRight: '-16px',
        marginTop: '-16px',
        height: '10px',
    },

    title: {
        fontSize: '24px',
        fontWeight: 'normal',
        color: '#282828',
    },
})

const InstructionsModal: FC<any> = (props) => {
    const { open, onClose, title, header, children } = props
    const classes = useModalStyles()

    return (
        <Dialog
            fullWidth
            scroll={'body'}
            open={open}
            onClose={onClose}
            aria-labelledby={title}
            aria-describedby={title}
        >
            <Box className={classes.modal}>
                {header ? (
                    header
                ) : (
                    <Box className={classes.modalHeader}>
                        <Box className={classes.closeIcon}>
                            <IconButton onClick={onClose} size="large">
                                <Icon name="Close" color="#959598" />
                            </IconButton>
                        </Box>
                        <Typography className={classes.title}>
                            {title}
                        </Typography>
                    </Box>
                )}

                {children}
            </Box>
        </Dialog>
    );
}

const useStyles = makeStyles((theme) =>
    createStyles({
        modalHeader: {
            padding: '32px 32px 16px 32px',
            width: '100%',
        },
        content: {
            color: theme?.palette?.common?.black,
            marginTop: '12px',
            padding: '20px 20px 32px 32px',
            backgroundColor: theme?.palette?.background?.paper,
            overflow: 'scroll',
            maxHeight: '500px',
            wordBreak: 'break-word',
        },
        title: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: theme?.palette?.common?.black,
        },
        closeIcon: {
            position: 'absolute',
            right: 20,
            top: 16,
            height: '10px',
            cursor: 'pointer',
        },
    }),
)

export type InstructionsProps = {
    title: string
    instructions: string
    button?: any
}
export const Instructions: FC<InstructionsProps> = (props) => {
    const { t } = useTranslation()

    const [showModal, setShowModal] = useState(false)

    const classes = useStyles()
    const theme = useTheme()

    const header = (
        <Box className={classes.modalHeader}>
            <Typography className={classes.title}>{props.title}</Typography>
            <Box className={classes.closeIcon}>
                <Icon
                    name="Close"
                    color={theme?.palette?.common?.black}
                    onClick={() => setShowModal(false)}
                />
            </Box>
        </Box>
    )

    const parsedHtml = ReactHtmlParser(DomPurify.sanitize(props.instructions))

    return (
        <Fragment>
            {props.button ? (
                props.button(setShowModal)
            ) : (
                <Button onClick={() => setShowModal(true)}>
                    {t('Common_MoreInfo')}
                </Button>
            )}
            <InstructionsModal open={showModal} header={header}>
                {parsedHtml && parsedHtml.length > 0 && (
                    <Box className={classes.content}>{parsedHtml}</Box>
                )}
            </InstructionsModal>
        </Fragment>
    )
}
